import { memo, useCallback, useEffect } from 'react'

import { AnimationControls } from './animation-controls'

import { ScoredDrawResultComplete, TicketResult } from '@API/game-results/types'
import { DrawAnimationResult } from '@Animation/types'

import { AnimationDrawInfoPanel } from '../draw-info-panel/animation-draw-info-panel'
import { TicketInfo } from '@Config/ticket-form-types'

import './styles.scss'
import Analytics, { AnalyticEvents } from '@Util/analytics'
import { RootState } from '@AppState/redux-store'
import { useDispatch, useSelector } from 'react-redux'
import { AnimationActions } from '@AppState/animation'
import { selectAnimationIdentifier } from '@Animation/utils'
import { useDebouncedCallback } from 'use-debounce'

export type TicketModeControlsProps = {
  animationReady: boolean
  animationResultIndex: number
  currentDrawingIndex: number
  latestDraw: number | null
  ticketInfo: TicketInfo
  ticketResult: TicketResult
  onDrawingIndexChange: (drawIndex: number) => void
}

export const TicketModeController = memo(
  (props: TicketModeControlsProps) => {
    const {
      animationResultIndex,
      currentDrawingIndex,
      latestDraw,
      ticketInfo,
      ticketResult,
      onDrawingIndexChange,
    } = props

    const { result } = useSelector((state: RootState) => state.animation)
    const dispatch = useDispatch()

    const debounceAnimationChange = useDebouncedCallback((config: DrawAnimationResult) => {
      dispatch(
        AnimationActions.playNewDraw({
          result: config,
          identifier: selectAnimationIdentifier(),
        }),
      )
    }, 500)

    let winningsThisDraw = 0
    let winningsThisTicket = 0
    let currentSelectedNumber = null
    if (
      ticketResult.scoredDrawResults[currentDrawingIndex] &&
      ticketResult.scoredDrawResults[currentDrawingIndex].drawingComplete
    ) {

      const winningNumbers = ticketResult.scoredDrawResults[currentDrawingIndex].winningNumbers
      let shouldHideZeroMatchWinnings = false
      let firstLastMatch = false

      const minTicketWinnings = winningNumbers.reduce((prev, curr) => {
        return Math.min(prev, curr.cumulativeTicketWinningsInCents)
      }, 999999999999)

      winningNumbers.forEach((val, idx) => {
        if(idx > animationResultIndex && val.cumulativeDrawingWinningsInCents < winningNumbers[animationResultIndex].cumulativeDrawingWinningsInCents){
          shouldHideZeroMatchWinnings = true
        }
      })

      if(!shouldHideZeroMatchWinnings && winningNumbers.length -1 !== animationResultIndex){
        shouldHideZeroMatchWinnings = winningNumbers[0].cumulativeDrawingWinningsInCents === winningNumbers[winningNumbers.length - 1].cumulativeDrawingWinningsInCents
        firstLastMatch = shouldHideZeroMatchWinnings
      }

      winningsThisDraw = shouldHideZeroMatchWinnings ? 0 :
        winningNumbers[animationResultIndex]
          .cumulativeDrawingWinningsInCents

      if(shouldHideZeroMatchWinnings){
        const lastDraw = ticketResult.scoredDrawResults[currentDrawingIndex-1]
        if(currentDrawingIndex === 0){
          winningsThisTicket = 0
        } else if (firstLastMatch && lastDraw.drawingComplete) {
          winningsThisTicket = lastDraw.winningNumbers[lastDraw.winningNumbers.length - 1].cumulativeTicketWinningsInCents
        } else {
          winningsThisTicket = minTicketWinnings
        }
      } else{
        winningsThisTicket =
          winningNumbers[animationResultIndex]
            .cumulativeTicketWinningsInCents
      }

      currentSelectedNumber =
        winningNumbers[animationResultIndex]
          .numberSelection
    }

    const buildDrawAnimationResultForDrawing = useCallback(
      (drawResult: ScoredDrawResultComplete): DrawAnimationResult => {
        return {
          winningNumbers: drawResult.winningNumbers
            .filter((res) => !res.addonName)
            .map((res) => {
              return {
                cumulativeDrawingWinningsInCents: res.cumulativeDrawingWinningsInCents,
                cumulativeTicketWinningsInCents: res.cumulativeTicketWinningsInCents,
                addonName: res.addonName,
                numberSelection: res.numberSelection,
              }
            }),
          ticketInfo: {
            baseSelectedNumbers: ticketInfo.numberSelections,
            miniGameSelectedNumbers: ticketInfo.miniGameAddOnSelection?.numberSelections ?? [],
            multiplierAddOn: ticketInfo.multiplierAddOnSelection?.name,
          },
          extraDrawAddOnResult: ticketInfo.extraDrawAddOnSelection
            ? drawResult.extraDrawAddOnResult
            : undefined,
          miniGameAddOnResult: ticketInfo.miniGameAddOnSelection
            ? drawResult.miniGameAddOnResult
            : undefined,
          multiplierAddOnResult: ticketInfo.multiplierAddOnSelection
            ? drawResult.multiplierAddOnResult
            : undefined,
          drawNumber: drawResult.drawingNumber,
        }
      },
      [ticketInfo],
    )

    useEffect(() => {
      if (!latestDraw) return

      const indexedDraw = ticketResult.scoredDrawResults[currentDrawingIndex]

      if (
        indexedDraw &&
        indexedDraw.drawingNumber !== result?.drawNumber &&
        indexedDraw.drawingComplete
      ) {
        const config = buildDrawAnimationResultForDrawing(indexedDraw)

        debounceAnimationChange(config)
      } else if (
        indexedDraw &&
        !indexedDraw.drawingComplete &&
        indexedDraw.drawingNumber > latestDraw
      ) {
        dispatch(AnimationActions.clearDrawResult())
      }
    }, [
      currentDrawingIndex,
      latestDraw,
      ticketResult.scoredDrawResults,
      dispatch,
      buildDrawAnimationResultForDrawing,
      result?.drawNumber,
      debounceAnimationChange,
    ])

    useEffect(() => {
      return () => {
        dispatch(AnimationActions.clearDrawResult())
      }
    }, [dispatch])

    const onNextDrawClick = useCallback(() => {
      const newResultIndex = currentDrawingIndex + 1

      if (newResultIndex >= ticketInfo.numberOfDraws) {
        return
      }
      Analytics.trackDrawing(AnalyticEvents.NEXT_DRAWING, result?.drawNumber ?? 0)
      onDrawingIndexChange(newResultIndex)
    }, [currentDrawingIndex, ticketInfo, onDrawingIndexChange, result?.drawNumber])

    const onPrevDrawClick = useCallback(() => {
      const newResultIndex = currentDrawingIndex - 1
      if (newResultIndex >= 0) {
        Analytics.trackDrawing(AnalyticEvents.PREVIOUS_DRAWING, result?.drawNumber ?? 0)
        onDrawingIndexChange(newResultIndex)
      }
    }, [currentDrawingIndex, onDrawingIndexChange, result?.drawNumber])

    return (
      <>
        <AnimationDrawInfoPanel
          drawNumber={
            result?.drawNumber ??
            ticketResult.scoredDrawResults[currentDrawingIndex]?.drawingNumber ??
            null
          }
          winsThisDraw={winningsThisDraw}
          winsTotal={winningsThisTicket}
          currentSelectedNumber={currentSelectedNumber}
        />

        <AnimationControls
          prevButtonControl={{
            onClick: onPrevDrawClick,
            disabled: currentDrawingIndex < 1,
          }}
          nextButtonControl={{
            onClick: onNextDrawClick,
            disabled: currentDrawingIndex + 1 >= ticketInfo.numberOfDraws,
          }}
        />
      </>
    )
  },
  (prevProps: TicketModeControlsProps, nextProps: TicketModeControlsProps) => {
    return JSON.stringify(prevProps) === JSON.stringify(nextProps)
  },
)
